import { P2GBox, P2GChip, P2GContainer, P2GIcon, P2GText, SvgTickCircle } from '@parcel2go/core-ui';
import { AppCard } from '~/components/card/app-card';
import { SidePanelContent } from '~/components/sidepanel-section';
import { Suspense, useEffect, useMemo, useState } from 'react';
import Loading from '~/components/loading/Loading';
import { OnboardingActionBar } from './onboarding-action-bar';
import { OnboardingTitle } from '../rules/onboarding-title';
import { OnboardingScreenContentWrapper } from '../../OnboardingScreenContentWrapper';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import useContentString from '~/hooks/use-content-string';

type props = {
  isXs: boolean;
  onDone: () => void;
};

const Row = ({ row, currentRow, children }: { row: number; currentRow: number; children: React.ReactNode }) => {
  const isDone = currentRow > row;
  return (
    <AppCard>
      <P2GBox p={2} sx={{ opacity: isDone ? 1 : 0.5 }}>
        <P2GText component="div" display="flex" flexWrap="nowrap" alignItems="center" gap={1}>
          <P2GIcon size="sm" palette={isDone ? 'success' : undefined}>
            {isDone ? <SvgTickCircle /> : <Loading />}
          </P2GIcon>
          {children}
        </P2GText>
      </P2GBox>
    </AppCard>
  );
};

const IntroScreen = ({ onDone }: props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const [counter, setCounter] = useState(0);
  const speed = useContentString('smartsend-setup-connection-speed-stats-string');

  const steps = useMemo(
    () => [
      {
        default: 'Checking to see if we’ve already got any details about you',
        key: 'SETUP_INTRO_STEP_CHECKING_DETAILS'
      },
      {
        default: 'Checking how many items a week you’re planning to send',
        key: 'SETUP_INTRO_STEP_CHECKING_ITEMS'
      },
      { default: 'Looking for any delivery defaults', key: 'SETUP_INTRO_STEP_CHECKING_DEFAULTS' }
    ],
    []
  );

  useEffect(() => {
    if (counter > steps.length) {
      onDone();
    }
    const timer = setTimeout(() => setCounter(counter + 1), 1500);
    return () => clearTimeout(timer);
  }, [counter, onDone, steps.length]);

  return (
    <>
      <OnboardingScreenContentWrapper>
        <P2GContainer maxWidth="md">
          <SidePanelContent>
            <OnboardingTitle text="We’re building your <span>account</span>" translationKey="SETUP_INTRO_TITLE" />
            <P2GText variant="subtitle1">
              {t('SETUP_INTRO_SUBTITLE', {
                defaultValue: "Sit tight, we're getting everything ready so you can start sending right away."
              })}
            </P2GText>
          </SidePanelContent>
        </P2GContainer>
        <P2GBox mt={4} mb={4}>
          <P2GChip label={t('SETUP_INTRO_CHIP_LABEL', { defaultValue: 'Off We Go...' })} />
        </P2GBox>
        <P2GContainer maxWidth="md">
          <P2GBox display="flex" flexDirection="column" gap={1}>
            {steps.map((step, index) => (
              <Row key={step.key} row={index} currentRow={counter}>
                {t(step.key, { defaultValue: step.default })}
              </Row>
            ))}
          </P2GBox>
        </P2GContainer>
      </OnboardingScreenContentWrapper>
      {speed && (
        <Suspense>
          <OnboardingActionBar>
            <P2GText variant="subtitle2">{speed}</P2GText>
          </OnboardingActionBar>
        </Suspense>
      )}
    </>
  );
};
export default IntroScreen;
